<script setup lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { AlertCircle, ChevronRight } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'

  export interface ValidationErrorItem {
    message: string
    sectionIndex?: number
    sectionUuid?: string
    sectionName?: string
    questionIndex?: number
    questionUuid?: string
    questionName?: string
    field?: string
  }

  const props = defineProps<{
    open: boolean
    errors: ValidationErrorItem[]
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'navigateToError', error: ValidationErrorItem): void
  }>()

  const { t } = useTranslation({
    en: {
      validationErrors: 'Validation Errors',
      errorsFound: '{count} error found | {count} errors found',
      fixErrorsDescription: 'Please fix the following errors before saving:',
      inSection: 'In section',
      inQuestion: 'In question',
      goToError: 'Go to error',
      close: 'Close'
    },
    de: {
      validationErrors: 'Validierungsfehler',
      errorsFound: '{count} Fehler gefunden | {count} Fehler gefunden',
      fixErrorsDescription: 'Bitte beheben Sie die folgenden Fehler vor dem Speichern:',
      inSection: 'Im Abschnitt',
      inQuestion: 'Bei der Frage',
      goToError: 'Zum Fehler',
      close: 'Schließen'
    }
  })

  const handleNavigate = (error: ValidationErrorItem) => {
    emit('navigateToError', error)
    emit('update:open', false)
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2 text-destructive">
          <AlertCircle class="h-5 w-5" />
          {{ t('validationErrors') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('fixErrorsDescription') }}
        </DialogDescription>
      </DialogHeader>

      <div class="max-h-80 space-y-3 overflow-y-auto py-4">
        <div
          v-for="(error, index) in errors"
          :key="index"
          class="rounded-lg border border-destructive/20 bg-destructive/5 p-4"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <!-- Location indicator -->
              <div
                v-if="error.sectionName || error.questionName"
                class="mb-2 flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
              >
                <template v-if="error.sectionName">
                  <span class="font-medium">{{ t('inSection') }}:</span>
                  <span class="truncate">{{ error.sectionName }}</span>
                </template>
                <template v-if="error.questionName">
                  <ChevronRight
                    v-if="error.sectionName"
                    class="h-3 w-3 flex-shrink-0"
                  />
                  <span class="font-medium">{{ t('inQuestion') }}:</span>
                  <span class="truncate">{{ error.questionName }}</span>
                </template>
              </div>

              <!-- Error message -->
              <p class="text-sm text-foreground">
                {{ error.message }}
              </p>
            </div>

            <!-- Navigate button -->
            <Button
              v-if="error.sectionUuid || error.questionUuid"
              variant="outline"
              size="sm"
              class="flex-shrink-0"
              @click="handleNavigate(error)"
            >
              {{ t('goToError') }}
            </Button>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="emit('update:open', false)"
        >
          {{ t('close') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
